/**
 * Shared EBR health banding — a phase's EBR ÷ target falls into one of five bands. Pure logic only;
 * callers map the band to their own color tokens (TimeReviewModal.jsx's theme-reactive CSS custom
 * properties vs. phaseLandscape.js's plain hex, since the latter renders outside the #aj-mgr scope
 * those tokens are defined in) rather than sharing color values directly. See docs/EBR_FRAMEWORK.md.
 *
 * `isFixedFee` matters (Matt, 2026-08-10, caught live on Forsyth Residence's Construction Documents
 * Phase reading "senior-heavy" at EBR $335/hr against a $125 target): for Hourly, EBR is the
 * blended ACTUAL billing rate of hours logged, so a high ratio genuinely means senior-heavy
 * staffing — the symmetric high/low bands are correct. For Fixed Fee, EBR is
 * `Contracted Fee ÷ Hours Expended` — a budget-PACE ratio, not a rate. A high ratio there just means
 * the phase hasn't used many hours relative to its budget yet (early in the phase, or genuinely
 * efficient) — never a problem, and never a staffing-mix signal at all. Only the LOW side (hours
 * running ahead of what the fee supports) is ever worth flagging for Fixed Fee; a high ratio is
 * always 'green', no matter how high.
 */
export function ebrBand(ratio, isFixedFee) {
  if (ratio == null) return null
  if (isFixedFee) {
    if (ratio < 0.6) return 'red-low'
    if (ratio < 0.9) return 'amber-low'
    return 'green'
  }
  if (ratio > 1.7) return 'red-high'
  if (ratio > 1.2) return 'amber-high'
  if (ratio < 0.6) return 'red-low'
  if (ratio < 0.9) return 'amber-low'
  return 'green'
}

export function ebrLabel(ratio, isFixedFee) {
  if (isFixedFee) {
    switch (ebrBand(ratio, true)) {
      case 'red-low': return '⚠ Well ahead of budget pace'
      case 'amber-low': return 'Running ahead of budget pace'
      case 'green': return 'On budget pace'
      default: return ''
    }
  }
  switch (ebrBand(ratio, false)) {
    case 'red-high': return '⚠ Too much senior time'
    case 'amber-high': return 'Too much senior time?'
    case 'red-low': return '⚠ Not enough senior time'
    case 'amber-low': return 'Not enough senior time?'
    case 'green': return 'Healthy mix'
    default: return ''
  }
}
